import { SupabaseClient } from '@supabase/supabase-js';
import { visibilityManager } from './visibilityManager';

class AuthRefreshManager {
  private refreshInterval: number | null = null;
  private supabaseClient: SupabaseClient | null = null;
  private readonly REFRESH_INTERVAL_MS = 3600000;
  private readonly ACTIVITY_THRESHOLD_MS = 300000;

  initialize(supabase: SupabaseClient) {
    this.supabaseClient = supabase;
    this.startIntelligentRefresh();

    console.log('[AuthRefreshManager] Initialized with 1-hour refresh interval');
  }

  private startIntelligentRefresh() {
    if (this.refreshInterval !== null) {
      return;
    }

    this.refreshInterval = window.setInterval(async () => {
      if (!this.supabaseClient) {
        return;
      }

      const isVisible = visibilityManager.getIsVisible();
      const isActive = visibilityManager.isRecentlyActive(this.ACTIVITY_THRESHOLD_MS);

      if (!isVisible || !isActive) {
        console.log('[AuthRefreshManager] Skipping token refresh - tab hidden or inactive');
        return;
      }

      try {
        const { data: { session }, error } = await this.supabaseClient.auth.getSession();

        if (error) {
          console.error('[AuthRefreshManager] Error checking session:', error);
          return;
        }

        if (session) {
          const expiresAt = session.expires_at;
          const now = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = expiresAt ? expiresAt - now : 0;

          if (timeUntilExpiry < 600) {
            console.log('[AuthRefreshManager] Token expiring soon, refreshing...');
            const { error: refreshError } = await this.supabaseClient.auth.refreshSession();

            if (refreshError) {
              console.error('[AuthRefreshManager] Error refreshing session:', refreshError);
            } else {
              console.log('[AuthRefreshManager] Token refreshed successfully');
            }
          } else {
            console.log('[AuthRefreshManager] Token still valid, no refresh needed');
          }
        }
      } catch (error) {
        console.error('[AuthRefreshManager] Unexpected error during refresh check:', error);
      }
    }, this.REFRESH_INTERVAL_MS);

    console.log('[AuthRefreshManager] Started intelligent refresh scheduler');
  }

  stop() {
    if (this.refreshInterval !== null) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('[AuthRefreshManager] Stopped refresh scheduler');
    }
  }
}

export const authRefreshManager = new AuthRefreshManager();
